import React, { useCallback, useContext, useState } from "react";

import AppContext from "~/common/AppContext";
import ThemeContext from "~/common/ThemeContext";
import { Button } from "~/ui";
import Checkbox from "~/ui/legacy/Checkbox";
import { ButtonLink, Modal } from "~/ui/legacyFellowIndex";

import { ReactComponent as IllustrationDark } from "./css/watching-empty-state-dark.svg";
import { ReactComponent as Illustration } from "./css/watching-empty-state.svg";
import { streamStore as store } from "./utils";

import styles from "./css/WatchModal.module.css";

export const SHOW_AGAIN_KEY = "showWatchIconAgain";

type Props = Omit<React.ComponentProps<typeof Modal>, "onClose"> & {
	onClose(): void; // just to make it required
};

export default function WatchModal({ onClose: propsOnClose, ...props }: Props) {
	const [showAgain, setShowAgain] = useState(true);
	const { isDarkMode } = useContext(ThemeContext);

	const { openSettingsModal } = useContext(AppContext);
	const openNotificationSettings = useCallback(() => {
		openSettingsModal("notifications");
	}, [openSettingsModal]);

	const updateStorage = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setShowAgain(!e.target.checked);
	}, []);

	const okRef = React.useRef<HTMLButtonElement>(null);
	const onClose = useCallback(() => {
		propsOnClose();

		store.set(SHOW_AGAIN_KEY, showAgain);
	}, [showAgain, propsOnClose]);

	return (
		<Modal width="28.75rem" onClose={onClose} initialFocusRef={okRef} {...props}>
			<Modal.Header onClose={onClose}>
				<Modal.Title>Watching Series</Modal.Title>
			</Modal.Header>
			<Modal.Body className={styles.modalBody}>
				{isDarkMode ? <IllustrationDark /> : <Illustration />}
				<h4>You are now watching this series</h4>
				<p className={styles.body}>
					You will be notified when updates occur. To customize notifications, visit your{" "}
					<ButtonLink onClick={openNotificationSettings}>settings</ButtonLink>.
				</p>
				<label className={styles.label}>
					<Checkbox checked={!showAgain} onChange={updateStorage} />
					Don&rsquo;t show me this again
				</label>
			</Modal.Body>
			<Modal.Footer>
				<Button size="normal" variant="primary" ref={okRef} onClick={onClose}>
					Ok
				</Button>
			</Modal.Footer>
		</Modal>
	);
}
